import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import {
  FormControl,
  FormGroup,
  FormRecord,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms'

import { isEmpty as _isEmpty, isEqual as _isEqual } from 'lodash/fp'

import { filter, map, tap } from 'rxjs'

import { FlxCheckThemeDirective, FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxCheckboxModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  CheckboxTemplateConfig,
  FormFieldsEnum,
  LabelAccessibilityProps,
  processDataValueFromPath$,
  prototypeInstanceMetaToPath,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxFieldOptionService } from '../../services/flx-field-option.service'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-checkbox-wrapper',
  imports: [
    CommonModule,
    FlxCheckboxModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  hostDirectives: [FlxCheckThemeDirective],
  templateUrl: './checkbox-wrapper.component.html',
  styleUrl: './checkbox-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxCheckboxWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #optionsService = inject(FlxFieldOptionService)
  #destroyRef = inject(DestroyRef)
  #cdr = inject(ChangeDetectorRef)

  templateConfig = input.required<CheckboxTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    selected: getThemeIcon(ThemeComponent.Checkbox, 'selectedIcon'),
    unselected: getThemeIcon(ThemeComponent.Checkbox, 'unselectedIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  fieldKey = computed(() => {
    const { key, prototypeInstanceMeta } = this.templateConfig()
    return prototypeInstanceMeta
      ? `${prototypeInstanceMetaToPath(prototypeInstanceMeta)}.${key}`
      : key
  })

  checkboxesFormGroup = new FormRecord({})

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  options: Signal<Array<{ label: string; value: string; enabled?: boolean }>>
  ariaLabel: Signal<string>

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  constructor() {
    effect(() => {
      const disabledState = this.disabledState()
      if (disabledState) {
        this.checkboxesFormGroup.disable({ emitEvent: false })
      } else {
        this.checkboxesFormGroup.enable({ emitEvent: false })
      }
    })
  }

  ngOnInit(): void {
    const config = this.templateConfig()
    const { processInstanceUuid, dataSource, prototypeInstanceMeta, reusableUiTemplateContext } =
      config

    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const { label, helper } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, config)
      this.options = toSignal(
        this.#optionsService
          .getOptionsObs(
            dataSource,
            processInstanceUuid,
            this.control().parent as FormGroup,
            config.flowxUuid,
            prototypeInstanceMeta,
            reusableUiTemplateContext,
            FormFieldsEnum.CHECKBOX
          )
          .pipe(
            map((options) =>
              options.map((option) => ({
                ...option,
                enabled: option.enabled !== undefined ? option.enabled : true,
              }))
            ),
            tap((options) => this.#setCheckGroupOptions(options))
          )
      )
    })

    // process data value updates
    processDataValueFromPath$(processInstanceUuid, this.fieldKey())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((processValue: Array<any>) => {
        const controlValue = this.#checkboxObjectToValuesArray(this.checkboxesFormGroup.value)
        if (
          (!_isEmpty(processValue) || !_isEmpty(controlValue)) &&
          !_isEqual(controlValue, processValue)
        ) {
          Object.entries(this.checkboxesFormGroup.controls).forEach(([key, control]) =>
            control.setValue(processValue?.includes(key), { emitEvent: false })
          )
          this.#cdr.markForCheck()
        }
      })

    // internal control updates => update the main control
    // only user interaction reaches this point: programmatic writes use `emitEvent: false`
    // and the value guard drops any echo that matches the main control, so enabling/disabling
    // the group never marks it dirty
    this.checkboxesFormGroup.valueChanges
      .pipe(
        map((value) => this.#checkboxObjectToValuesArray(value)),
        filter((value) => !_isEqual(value, this.control().value)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((value) => {
        this.control().setValue(value)
        this.control().markAsDirty()
      })
  }

  #setCheckGroupOptions(options: Array<{ value: string; label: string }>): void {
    const controls = Object.keys(this.checkboxesFormGroup.controls)
    // remove old options
    controls.forEach((key) => {
      this.checkboxesFormGroup.removeControl(key, { emitEvent: false })
    })

    options.forEach((option) => {
      this.checkboxesFormGroup.addControl(
        option.value,
        new FormControl({
          value: this.#checkValue(option, this.control().value),
          disabled: this.control().disabled,
        }),
        { emitEvent: false }
      )
    })
  }

  #checkValue({ value }: any, values?: any[] | unknown): boolean {
    if (!Array.isArray(values)) {
      return values === value
    }
    return values.includes(value)
  }

  #checkboxObjectToValuesArray(object: Record<string, boolean>): string[] {
    return Object.entries(object)
      .filter(([, value]) => value)
      .map(([key]) => key)
  }
}
